// A veces electron deja de generar eventos de raton (mouseup, mousedown, click) a partir de los eventos (touchstart, touchend).
// Parece que es un bug en electron con algunos drivers de pantallas táctiles, y se manifiesta sobre todo cuando la ventana de
// electron pierde el foco.
//
// Una forma de reproducirlo es usando un botón de cobro directo y un cashlogy. Si se pulsa el botón de cobro directo, se cancela
// la operación con cashlogy, y mientras se cancela se pulsa rápidamente varias veces el botón de cobro directo, se acaba colgando
// la aplicación, quedando el botón "presionado" y la pantalla bloqueda.
//
// Es este hilo de github aportan varias soluciones, pero la única que nos ha funcionado es aplicar este hack para convertir
// por código los eventos touch en eventos mouse: https://github.com/electron/electron/issues/8725#issuecomment-350786200
//
// El problema es que rompe el scroll nativo y, aunque permite hacer scroll, se pierde el scroll inercial y la experiencia de
// usuario se resiente, así que sólo se habilita como último recurso si el usuario lo ha activado a través de la configuración
// de pos.settings.xml.

// OJO: El código está lleno de anys porque está copiado del hilo de github, probado, y no hemos querido cambiar nada.

export function fixTouchEvents(sendInputEvent: (event: Electron.MouseInputEvent | Electron.KeyboardInputEvent | Electron.MouseWheelInputEvent) => void) {

	// List of active touches.
	const activeTouches = {} as any;
	// Click counter
	let numClicks = 0;
	// Timer for reseting click counter, so it may be cleared.
	let clickCountTimer: any = null;
	// Reference to the last touch object received at a location;
	let lastTouch: unknown = null;
	// Distance away from initial touch that constitutes cancelation, or disqualifies from doubleclick.
	const moveThreshold = 10;

	const handlers = {} as any;

	// Handler for touchstart events. Tracks active touches.
	handlers.touchstart = function (event: any) {
		// Store touches for later use.
		const touches = Array.from(event.changedTouches);
		touches.forEach((touch: any) => {
			touch.defaultPrevented = event.defaultPrevented;
			activeTouches[touch.identifier] = touch;
		});
	};

	// Handler for touchend events. Tells main process to generate mouse events.
	handlers.touchend = function (event: any) {
		// Check if somebody tried to prevent touches from becoming clicks.
		let defaultPrevented = event.defaultPrevented;
		// Prevent touches from being translated to clicks, do it ourselves
		event.preventDefault();
		// Translate corresponding touches to mouse events.
		const touches = Array.from(event.changedTouches);
		touches.forEach((end: any) => {
			const start = activeTouches[end.identifier];
			// Check if defualt was prevented in touchstart also.
			defaultPrevented = defaultPrevented || start.defaultPrevented;
			delete activeTouches[end.identifier];

			// Don't fire click if the default action was prevented by user
			//   or if the touch was otherwise cancelled (cancel or too much movement)
			if (defaultPrevented !== true && start.cancelled !== true) {
				const x = Math.round(end.clientX),
					y = Math.round(end.clientY);
				// Keep track of the number of clicks within 500ms of eachother closer than the moveThreshold
				// (for double clicks)
				if (numClicks === 0 || dist(lastTouch, end) <= moveThreshold) {
					numClicks++;
				} else { // numClicks > 0 && dist > moveThreshold
					clearTimeout(clickCountTimer);
					numClicks = 1;
				}
				lastTouch = end;
				if (numClicks === 1) {
					clickCountTimer = setTimeout(function () {
						numClicks = 0;
						lastTouch = null;
						clickCountTimer = null;
					}, 500);
				}
				falsifyClick(x, y, numClicks);
			}
		});
	};

	// Capture touchstart, touchend events first thing when they happens.
	document.addEventListener('touchstart', captureHandler, {
		capture: true,
		passive: false
	});
	document.addEventListener('touchend', captureHandler, {
		capture: true,
		passive: false
	});

	function captureHandler(event: Event) {
		const stopPropagation = event.stopPropagation;
		const stopImmediatePropagation = event.stopImmediatePropagation;
		// Ensure that our handler is called for touchstart, even if propagation of the event is stopped.
		//   Keeps everything clean.
		event.stopPropagation = function () {
			stopPropagation.apply(event);
			handlers[event.type](event, true);
		};
		event.stopImmediatePropagation = function () {
			stopImmediatePropagation.apply(event);
			handlers[event.type](event, true);
		};
	}

	// Listen for bubbling touchstart events on the document.
	document.addEventListener('touchstart', handlers.touchstart, {
		capture: false,
		passive: false
	});

	// Listen for bubbling touchend events on the document.
	document.addEventListener('touchend', handlers.touchend, {
		capture: false,
		passive: false
	});

	// Cancel corresponding touches if touch was canceled.
	document.addEventListener('touchcancel', function (event) {
		const touches = Array.from(event.changedTouches);
		touches.forEach((touch) => {
			activeTouches[touch.identifier].cancelled = true;
		});
	}, {
		capture: true,
		passive: true
	});

	// Prevent default touchmove actions (scrolling)
	document.addEventListener('touchmove', function (event) { event.preventDefault(); }, {
		capture: true,
		passive: false
	});
	// Cancel corresponding touches if touch moved too much.
	// Handle scrolling.
	document.addEventListener('touchmove', function (event) {
		const touches = Array.from(event.changedTouches);
		touches.forEach((touch) => {
			const start = activeTouches[touch.identifier];
			if (start.scrolling) {
				const target = start.scrollTarget;
				target.scrollTop += start.scrollY - touch.clientY;
				target.scrollLeft += start.scrollX - touch.clientX;
				start.scrollX = touch.clientX;
				start.scrollY = touch.clientY;
				return;
			}
			if (start.cancelled) {
				return;
			}

			if (dist(start, touch) > moveThreshold) {
				start.cancelled = true;
				start.scrolling = true;
				const target = start.scrollTarget = getScrollParent(start.target);
				target.scrollTop += start.clientY - touch.clientY;
				target.scrollLeft += start.clientX - touch.clientX;
				start.scrollX = touch.clientX;
				start.scrollY = touch.clientY;
			}
		});
	}, {
		capture: true,
		passive: true
	});

	function falsifyClick(x: number, y: number, numClicks: number) {
		sendInputEvent({
			type: 'mouseMove',
			x: x,
			y: y
		});
		sendInputEvent({
			type: 'mouseDown',
			x: x,
			y: y,
			button: 'left',
			clickCount: numClicks
		});
		sendInputEvent({
			type: 'mouseUp',
			x: x,
			y: y,
			button: 'left',
			clickCount: numClicks
		});
	}

	// Calculates the distance between two touches.
	function dist(touch1: any, touch2: any) {
		return Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
	}

	// https://stackoverflow.com/questions/35939886
	function getScrollParent(element: any, includeHidden?: any) {
		let style = getComputedStyle(element);
		const excludeStaticParent = style.position === 'absolute';
		const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

		if (style.position === 'fixed') return document.body;
		for (let parent = element; (parent = parent.parentElement);) {
			style = getComputedStyle(parent);
			if (excludeStaticParent && style.position === 'static') {
				continue;
			}
			if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) return parent;
		}

		return document.body;
	}
}

